import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { COUNTRIES, Country, getCountryName } from '../data/countries';
import { useLanguage } from '../contexts/LanguageContext';

interface CountrySelectorProps {
  selectedCountry: string | null;
  onCountryChange: (countryCode: string | null) => void;
  className?: string;
  placeholder?: string;
  allowGlobal?: boolean; // Allow "Global" option (no country restriction)
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  selectedCountry,
  onCountryChange,
  className = "",
  placeholder = "Seleccionar país",
  allowGlobal = true
}) => {
  const { language, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Filter countries based on search term
  const filteredCountries = COUNTRIES.filter(country => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(searchLower) ||
      country.nameEn.toLowerCase().includes(searchLower) ||
      country.code.toLowerCase().includes(searchLower)
    );
  });

  // Get selected country object
  const selectedCountryObj = selectedCountry ? COUNTRIES.find(c => c.code === selectedCountry) : null;

  // Handle country selection
  const handleCountrySelect = (countryCode: string | null) => {
    onCountryChange(countryCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <Globe size={16} className="text-gray-400 mr-2 flex-shrink-0" />
          <span className="text-sm text-left">
            {selectedCountryObj ? (
              <>
                <span className="mr-2">{selectedCountryObj.flag}</span>
                {getCountryName(selectedCountryObj, language)}
              </>
            ) : (
              <span className="text-gray-500">
                {allowGlobal && !selectedCountry ? t('common.globalCountryOption') : placeholder}
              </span>
            )}
          </span>
        </div>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar país..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Country options */}
          <div className="max-h-64 overflow-y-auto">
            {allowGlobal && (
              <button
                type="button"
                onClick={() => handleCountrySelect(null)}
                className={`w-full flex items-center px-3 py-2 text-sm text-left hover:bg-gray-100 ${
                  !selectedCountry ? 'bg-primary bg-opacity-10 font-medium text-primary' : 'text-gray-700'
                }`}
              >
                <Globe size={16} className="mr-3 text-gray-400" />
                <span className="flex-grow">{t('common.globalCountryOption')}</span>
                {!selectedCountry && <Check size={16} className="ml-auto text-primary" />}
              </button>
            )}
            
            {filteredCountries.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                No se encontraron países
              </div>
            ) : (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country.code)}
                  className={`w-full flex items-center px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${
                    selectedCountry === country.code 
                      ? 'bg-primary bg-opacity-10 font-medium text-primary' 
                      : 'text-gray-700'
                  }`}
                >
                  <span className="text-lg mr-3">{country.flag}</span>
                  <span className="flex-grow">{getCountryName(country, language)}</span>
                  {selectedCountry === country.code && (
                    <Check size={16} className="ml-auto text-primary" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;